<template>
  <div>
    <div v-if="label">
      <p class="tw-mb-1 tw-block tw-text-xs">{{ label }}</p>
    </div>
    <div v-if="subLabel" class="tw-mb-1 tw-block tw-text-xs tw-text-slate-500">
      {{ subLabel }}
    </div>
    <div v-if="image" class="tw-flex tw-items-center tw-rounded tw-border tw-p-3">
      <img :src="image" alt="Uploaded Image" class="tw-mr-2 tw-h-4 tw-object-contain" />
      <div
        data-testid="remove-image-cta"
        class="tw-cursor-pointer tw-text-xs tw-font-medium tw-text-black hover:tw-text-deep-orange-500"
        @click="handleRemoveImage"
      >
        Remove Image
      </div>
    </div>
    <div v-else class="tw-relative">
      <div v-studio-click-outside="closeMenu" class="tw-relative">
        <button
          data-testid="upload-image-cta"
          class="tw-w-full tw-cursor-pointer tw-rounded tw-border tw-p-3 tw-text-left tw-text-xs tw-font-medium tw-text-black hover:tw-text-deep-orange-500"
          :class="{ 'tw-text-red-500': required }"
          @click="isMenuOpen = !isMenuOpen"
        >
          Add Image
        </button>
        <div
          v-if="isMenuOpen"
          :class="[
            'tw-absolute tw-top-full tw-z-[100] tw-mt-1 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-white tw-text-sm tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none dark:tw-bg-black',
            dropdownPosition === 'right' ? 'tw-right-0' : 'tw-left-0',
            currentView === 'brandKit' && 'tw-max-h-[500px]'
          ]"
        >
          <div v-if="currentView === 'main'">
            <FilestackS3
              :s3path="s3BucketPath || orgS3Bucket"
              :acceptedTypes="acceptedTypes"
              :imageValidation="imageValidation"
              :replaceSpacesInFileName="true"
              @fileuploaded="onFileUpload"
              @onOpen="handlePickerOpen"
              @close="handlePickerClose"
              @cancelUpload="handlePickerClose"
            >
              <div
                data-testid="upload-image-cta"
                class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left hover:tw-bg-muted"
              >
                <IconUpload class="tw-h-4 tw-w-4" />
                Upload Image
              </div>
            </FilestackS3>
            <button
              class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left hover:tw-bg-muted"
              @click="currentView = 'brandKit'"
            >
              <IconBriefcase class="tw-h-4 tw-w-4" />
              Brand Kit
              <IconChevronRight class="tw-ml-auto tw-h-4 tw-w-4" />
            </button>
          </div>
          <div v-else-if="currentView === 'brandKit'" class="tw-max-h-[500px] tw-overflow-y-auto">
            <div
              class="tw-sticky tw-top-0 tw-flex tw-items-center tw-gap-2 tw-border-b tw-bg-white tw-px-3 tw-py-2 dark:tw-bg-black"
            >
              <button
                class="hover:tw-text-primary tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-muted-foreground"
                @click="currentView = 'main'"
              >
                <IconChevronLeft class="tw-h-4 tw-w-4" />
                Back
              </button>
            </div>
            <BrandKitGoToSelectPrompt
              v-if="!selectedBrandKit"
              class="tw-m-3"
              @goToSelectBrandKit="handleGoToSelectBrandKit"
            />
            <div v-else class="tw-space-y-6 tw-p-3">
              <BrandKitImageGrid
                title="Logos"
                :images="logos"
                defaultAlt="Logo"
                imageClass="tw-w-full tw-h-full tw-object-contain tw-p-2"
                overlayClass="tw-absolute tw-inset-0 tw-bg-black/0 group-hover:tw-bg-black/10 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                @select="handleBrandKitImageSelected"
              />

              <BrandKitImageGrid
                title="Photography"
                :images="photographyElements"
                defaultAlt="Brand kit asset"
                imageClass="tw-w-full tw-h-full tw-object-cover"
                overlayClass="tw-absolute tw-inset-0 tw-bg-black/0 group-hover:tw-bg-black/20 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                @select="handleBrandKitImageSelected"
              />

              <BrandKitImageGrid
                title="Design Elements"
                :images="designElements"
                defaultAlt="Design Element"
                imageClass="tw-w-full tw-h-full tw-object-cover"
                overlayClass="tw-absolute tw-inset-0 tw-bg-black/0 group-hover:tw-bg-black/20 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                @select="handleBrandKitImageSelected"
              />

              <div v-if="!hasAnyImage" class="tw-py-12 tw-text-center tw-text-muted-foreground">
                <p class="tw-text-sm">No images available in brand kit</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BrandKit } from '@goldcast/api/backend';
import { IconUpload, IconBriefcase, IconChevronLeft, IconChevronRight } from '@tabler/icons-vue';
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

import BrandKitGoToSelectPrompt from './BrandKitGoToSelectPrompt.vue';
import BrandKitImageGrid from './BrandKitImageGrid.vue';

import FilestackS3 from '@/components/core/Filestack/FilestackS3.vue';
import { CurrentOrganization } from '@/store/modules/org';
import { BrandKitAsset } from '@/studio/brandKit/store/types';
import { STATIC_ASSETS } from '@/utils/constants';

const props = withDefaults(
  defineProps<{
    label?: string;
    subLabel?: string;
    image?: string;
    required?: boolean;
    acceptedTypes?: string[];
    imageValidation?: any;
    s3BucketPath?: string;
    dropdownPosition?: 'left' | 'right';
  }>(),
  {
    label: '',
    subLabel: '',
    image: '',
    required: false,
    acceptedTypes: () => ['.jpg', '.jpeg', '.webp', '.png', '.ico'],
    imageValidation: undefined,
    s3BucketPath: '',
    dropdownPosition: 'left'
  }
);

const emit = defineEmits<{
  changeImageUrl: [url: string];
}>();

const store = useStore();
const isMenuOpen = ref(false);
const isPickerOpen = ref(false);
const currentView = ref<'main' | 'brandKit'>('main');

const getCurrentOrg = computed<CurrentOrganization>(() => store.getters['org/getCurrentOrg']);
const selectedBrandKit = computed<BrandKit | null>(() => store.state.studioBrandKit.selectedBrandKit);

const orgS3Bucket = computed<string>(() => {
  return `${STATIC_ASSETS.SOCIAL_MEDIA_THUMBNAIL_PATH}/organization/${getCurrentOrg.value?.id}/`;
});

onMounted(async () => {
  await store.dispatch('studioBrandKit/ensureBrandKitsLoaded');
});

const logos = computed<BrandKitAsset[]>(() => {
  return (selectedBrandKit.value?.logos ?? []) as BrandKitAsset[];
});

const photographyElements = computed<BrandKitAsset[]>(() => {
  return (selectedBrandKit.value?.photography_elements ?? []) as BrandKitAsset[];
});

const designElements = computed<BrandKitAsset[]>(() => {
  return (selectedBrandKit.value?.design_elements ?? []) as BrandKitAsset[];
});

const hasAnyImage = computed<boolean>(() => {
  return logos.value.length > 0 || photographyElements.value.length > 0 || designElements.value.length > 0;
});

async function onFileUpload(files: any) {
  const { filesUploaded, filesFailed } = files;
  if (filesFailed?.length || filesUploaded?.length === 0) {
    await store.dispatch('studioToast/triggerToast', {
      msg: 'File Upload Failed',
      mode: 'error'
    });
    return;
  }

  const resourceKey = filesUploaded[0]?.key;
  if (!resourceKey) {
    store.dispatch('studioToast/triggerToast', {
      msg: 'File upload completed but no resource key was returned. Please try again.',
      mode: 'error'
    });
    return;
  }

  const url = `${STATIC_ASSETS.CLOUDFRONT_URL}/${resourceKey}`;
  emit('changeImageUrl', url);
  isMenuOpen.value = false;
}

function handleBrandKitImageSelected(imageUrl: string) {
  emit('changeImageUrl', imageUrl);
  isMenuOpen.value = false;
  currentView.value = 'main';
}

function handleRemoveImage() {
  emit('changeImageUrl', '');
}

function closeMenu() {
  if (isPickerOpen.value) {
    return;
  }
  isMenuOpen.value = false;
  currentView.value = 'main';
}

function handlePickerOpen() {
  isPickerOpen.value = true;
}

function handlePickerClose() {
  isPickerOpen.value = false;
}

function handleGoToSelectBrandKit() {
  isMenuOpen.value = false;
  currentView.value = 'main';
  store.commit('branding/setSection', { sectionId: 'brandKit', moduleId: 'selectBrandKit' });
}
</script>
